import { Component, LOCALE_ID, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import {
  LucideAngularModule,
  Heart,
  Play,
  Settings,
  CheckCircle,
} from 'lucide-angular';
import { ModalComponent } from './shared/components/modal/modal.component';
import { AuthService } from './core/services/auth.service';
import { TokenRefreshService } from './core/services/token-refresh.service';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import * as AuthActions from './store/auth/auth.actions';
import { selectIsAuthenticated } from './store/auth/auth.selectors';
import { EnvironmentService } from './core/services/environment.service';
import { selectCountries } from './store/referentiel/referentiel.selectors';
import { loadCountries } from './store/referentiel/referentiel.actions';
import { ModalService } from './core/services';
import * as SettingsActions from './store/settings/settings.actions';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LucideAngularModule, ModalComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [{provide: LOCALE_ID, useValue: 'fr' }
],
})
export class AppComponent implements OnInit {
  title = 'passeport-web';

  readonly Heart = Heart;
  readonly Play = Play;
  readonly Settings = Settings;
  readonly CheckCircle = CheckCircle;

  countries: any[] = [];
  showCountryModal = false;
  private platformId = inject(PLATFORM_ID);

  constructor(
    private authService: AuthService,
    private tokenRefreshService: TokenRefreshService,
    private store: Store,
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private titleService: Title,
    private modalService: ModalService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Définir le titre de l'onglet selon l'environnement
    this.setDocumentTitle();

    // Vérifier si l'utilisateur est connecté (uniquement côté client)
    if (
      isPlatformBrowser(this.platformId) &&
      this.authService.isAuthenticated()
    ) {
      console.log('Utilisateur connecté détecté, récupération des données...');
      // Charger les données utilisateur dans le store
      this.store.dispatch(AuthActions.loadCurrentUser());
      // Démarrer la gestion automatique du refresh token
      this.tokenRefreshService.startTokenRefresh();
      // Test de l'intercepteur avec une requête simple
      this.testInterceptor();
    } else {
      console.log('Aucun utilisateur connecté');
    }
    this.checkDefaultCountry();
    this.loadSettings();
  }

  private async checkDefaultCountry() {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      // Ne pas ouvrir le modal de choix de pays sur la page /liens (avec ou sans préfixe pays)
      const currentUrl = this.router.url;
      if (currentUrl === '/liens' || /\/[^/]+\/liens$/.test(currentUrl)) {
        return;
      }

      const defaultCountry = localStorage.getItem('defaultCountry');
      if (!defaultCountry) {
        // Import dynamique du composant
        const { CountriesModalComponent } = await import(
          './shared/components/modal/countries-modal/countries-modal.component'
        );
        this.modalService.open({
          component: CountriesModalComponent,
          width: '800px',
          title: 'Connexion',
          closable: true,
          data: this.countries,
        });
      }
    }
  }

  private setDocumentTitle(): void {
    const appName = this.environmentService.getAppName();
    this.titleService.setTitle(appName);
  }

  private testInterceptor(): void {
    // Test simple pour vérifier que l'intercepteur fonctionne (uniquement côté client)
    if (isPlatformBrowser(this.platformId)) {
      console.log("🧪 Test de l'intercepteur...");
      this.http.get('https://httpbin.org/headers').subscribe({
        next: (response) => {
          console.log('✅ Test intercepteur réussi:', response);
        },
        error: (error) => {
          console.log('❌ Test intercepteur échoué:', error);
        },
      });
    }
  }

  private loadSettings(): void {
    console.log('📋 Récupération des settings...');
    this.store.dispatch(SettingsActions.loadSettings());
  }
}
